import { useGoogleMap } from "#/hook/useGoogleMap";
import { type Device } from "#/types/device";
import { sanitize } from "#/utils/StringUtils";
import { Accordion } from "@mantine/core";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { IconDeviceMobile, IconExternalLink } from "@tabler/icons-react";
import { useClient } from "#/hook/useClient";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { useTranslation } from "react-i18next";

export interface Props {
  device: Device;
  detailed?: boolean;
  collapsible?: boolean;
}
const DeviceBasicInfoCard = ({
  device,
  detailed = false,
  collapsible = false,
}: Props) => {
  const { isLoaded } = useGoogleMap();
  const { client } = useClient();
  const { t } = useTranslation();

  const longitude = device.longitude && Number(device.longitude) !== 0 ? Number(device.longitude) : (device.site?.longitude ? Number(device.site.longitude) : null);
  const latitude = device.latitude && Number(device.latitude) !== 0 ? Number(device.latitude) : (device.site?.latitude ? Number(device.site.latitude) : null);

  const content = (
    <div className="w-full grid grid-cols-2">
      <div className="w-full grid grid-cols-1 gap-4 p-4">
        <div className=" flex flex-col">
          <span className="uppercase text-xs">{t("devices.info.hardware")}</span>
          <b className="break-all text-sm capitalize">
            {sanitize((device?.hardware_type ?? "").replace(/^chainway_/i, ""))}
          </b>
        </div>
        <div className=" flex flex-col">
          <span className="uppercase text-xs">{t("devices.info.mac")}</span>
          <b className="break-all text-sm">{device?.mac_address}</b>
        </div>
        <div className=" flex flex-col">
          <span className="uppercase text-xs">{t("devices.info.serial")}</span>
          <b className="break-all text-sm">{device?.serial_number}</b>
        </div>
        <div className=" flex flex-col">
          <span className="uppercase text-xs">{t("devices.info.ip")}</span>
          <b className="break-all text-sm">{device?.ip_address ?? "--"}</b>
        </div>

        <div className=" flex flex-col">
          <span className="uppercase text-xs">{t("devices.info.lastActive")}</span>
          <b className="break-all text-sm">
            {device.last_seen
              ? formatDateTime(device.last_seen, client)
              : "--"}
          </b>
        </div>

        {device.site && (
          <div className=" flex flex-col">
            <span className="uppercase text-xs">{t("devices.info.site")}</span>
            <div
              className="flex items-center gap-2 cursor-pointer hover:underline w-fit"
              onClick={() => window.open(`/sites/${device?.site?.id}`, '_blank')}
            >
              <b className="break-all text-sm">{device?.site?.name ?? "--"}</b>
              <IconExternalLink size={14} className="opacity-60" />
            </div>
            <p className="break-all text-sm">
              {device?.site?.location_text ?? "--"}
            </p>
          </div>
        )}
      </div>
      {detailed &&
        isLoaded &&
        longitude != null &&
        latitude != null && (
          <div className="p-2 flex flex-col gap-2 ">
            <div className="w-full h-full rounded-lg overflow-hidden border-2 border-gray-300">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={{
                  lat: latitude,
                  lng: longitude,
                }}
                zoom={14}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                }}
              >
                <Marker
                  position={{
                    lat: latitude,
                    lng: longitude,
                  }}
                  title={`MAC: ${device.mac_address}\nIP: ${device.ip_address ?? "--"}\nSerial: ${device.serial_number}`}
                />
              </GoogleMap>
            </div>
            <span className="text-2xs font-bold mx-auto text-black/70 italic">{t("devices.mapDescription")}</span>
          </div>
        )}
    </div>
  );

  if (collapsible) {
    return (
      <Accordion.Item value="device_info">
        <Accordion.Control>{t("devices.deviceInformation")}</Accordion.Control>
        <Accordion.Panel>{content}</Accordion.Panel>
      </Accordion.Item>
    );
  }

  return (
    <div className="w-full h-fit flex flex-col bg-client-color/1 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-2 text-sm font-extrabold uppercase bg-client-color/10 px-4 py-3">
        <IconDeviceMobile size={18} />
        <span>{t("devices.rfidScanner")}</span>
      </div>
      {content}
    </div>
  );
};

export default DeviceBasicInfoCard;
